import ReactDOM from 'react-dom';

import {
  editType,
  shortCut,
  operandType,
  ChartBoxNumber,
  ComposeKey,
  UI_INTERACTION,
  CheckBoxAll,
  keyboardDirection,
  POSITION_MOVE_TYPE,
  validationKeyDownType,
  ShapeReviewRawAndEventCalledCase,
} from 'constant/ShapeReviewConst';
import { ECTOPIC_TYPE } from 'constant/EventConst';

import { BeatEventSelectionInfo } from 'component/hook/useChartList';

import {
  CheckBoxStatus,
  ActivePanelType,
} from 'redux/container/fragment/test-result/shape-review/ShapeReviewFragmentContainer';

import { IShapeReviewSidePanelEventGroup } from 'component/fragment/test-result/shape-review/FormsPanel/FormsPanelListFragment';

import { onKeyDown } from '@type/commonType';
import { EctopicType } from '@type/eventType';
import {
  ChartListItem,
  Checkbox,
  LastSelectedSectionInfo,
  Matrix,
  SelectedItemInfo,
} from 'redux/duck/shapeReview/shapeReviewDuckType';
import { WaveformIndex } from '@type/types';

export function validationKeyDown({
  movedChartIndex,
  maxIndexOfPanel,
  chartList,
  isKeydownWithoutComposeKey,
  isKeydownWithCtrl,
  isKeydownWithShift,
  panelType,
  handleSetPage,
}: {
  movedChartIndex: number;
  maxIndexOfPanel: number;
  chartList: ChartListItem[];
  isKeydownWithoutComposeKey: boolean;
  isKeydownWithCtrl: boolean;
  isKeydownWithShift: boolean;
  panelType: ActivePanelType;
  handleSetPage: React.Dispatch<
    React.SetStateAction<{
      panelType: ActivePanelType;
      setPageType: POSITION_MOVE_TYPE;
    }>
  >;
}): { type: validationKeyDownType; doReturn: boolean }[] {
  let validationResult: { type: validationKeyDownType; doReturn: boolean }[] =
    [];
  // shift + 방향키 이동시 panel size 미만, 초과시
  if (movedChartIndex < 0 && isKeydownWithShift) {
    validationResult.push({
      type: validationKeyDownType.UNDER_PANEL_SIZE_WITH_SHIFT_KEY,
      doReturn: true,
    });
  }
  if (
    (movedChartIndex > maxIndexOfPanel ||
      movedChartIndex > chartList.length - 1) &&
    isKeydownWithShift
  ) {
    validationResult.push({
      type: validationKeyDownType.OVER_PANEL_SIZE_WITH_SHIFT_KEY,
      doReturn: false,
    });
  }
  if (
    (movedChartIndex > maxIndexOfPanel ||
      movedChartIndex > chartList.length - 1) &&
    isKeydownWithCtrl
  ) {
    validationResult.push({
      type: validationKeyDownType.OVER_PANEL_SIZE_WITH_CTRL_KEY,
      doReturn: false,
    });
  }
  // 페이지 이동
  if (movedChartIndex < 0 && isKeydownWithoutComposeKey) {
    validationResult.push({
      type: validationKeyDownType.MOVE_PREV_PAGE,
      doReturn: false,
    });
  }
  if (
    (movedChartIndex > maxIndexOfPanel ||
      movedChartIndex > chartList.length - 1) &&
    isKeydownWithoutComposeKey
  ) {
    validationResult.push({
      type: validationKeyDownType.MOVE_NEXT_PAGE,
      doReturn: true,
    });
  }

  //
  if (movedChartIndex < 0 || movedChartIndex > maxIndexOfPanel) {
    validationResult.push({
      type: validationKeyDownType.OVER_PANEL_SIZE,
      doReturn: true,
    });
  }

  if (movedChartIndex > chartList.length - 1) {
    validationResult.push({
      type: validationKeyDownType.OVER_CHART_LIST_LENGTH,
      doReturn: false,
    });
  }

  return validationResult;
}

export function makeSetSelectedItemList({
  ectopicType,
  chartItemInfo,
  selectedItemList = new Map(),
  lastSelectedSectionInfo,
  movedSelectedItem,
  setSelectedItemList,
}: {
  ectopicType: EctopicType;
  chartItemInfo: BeatEventSelectionInfo;
  selectedItemList: Map<number, SelectedItemInfo>;
  lastSelectedSectionInfo: LastSelectedSectionInfo;
  movedSelectedItem: SelectedItemInfo;
  setSelectedItemList: React.Dispatch<
    React.SetStateAction<Map<number, SelectedItemInfo>>
  >;
}) {
  if (!lastSelectedSectionInfo[0]) return;

  let selectedSectionInfo = getSelectionInfo(
    lastSelectedSectionInfo[0], // 선택 구간의 begin
    movedSelectedItem // shift + click으로 선택한 지점
  );
  let newState = new Map<number, SelectedItemInfo>();

  // step1: 선택 구간의 begin과 shift + click으로 선택한 지점 설정
  for (let i = selectedSectionInfo.start; i <= selectedSectionInfo.end; i++) {
    const matrixRow = i / chartItemInfo.columnNumber;
    const matrixColumn = i % chartItemInfo.columnNumber;
    const matrix: Matrix = [Number.parseInt(String(matrixRow)), matrixColumn];
    let checkbox: Checkbox = undefined;

    const isShiftClick = lastSelectedSectionInfo[1] !== undefined;
    const lastClickInfo = lastSelectedSectionInfo[
      isShiftClick ? 1 : 0
    ] as SelectedItemInfo;
    const firstCheckboxOfLastSelectedSectionInfo = selectedItemList.get(
      lastClickInfo.index
    )?.checkbox;

    if (
      firstCheckboxOfLastSelectedSectionInfo &&
      firstCheckboxOfLastSelectedSectionInfo.filter((v) => !v).length !== 0
    ) {
      checkbox = firstCheckboxOfLastSelectedSectionInfo;
    } else {
      checkbox = getCheckBoxByEctopicType(ectopicType);
    }

    newState.set(i, {
      index: i,
      matrix,
      checkbox,
    });
  }

  setSelectedItemList((prev) => {
    // step2: 이미 click, shift+click이 모두 선택 된 경우 해당 section 제거
    let prevState = new Map(prev);
    if (!!lastSelectedSectionInfo[0] && !!lastSelectedSectionInfo[1]) {
      let selectedSectionInfo = getSelectionInfo(
        lastSelectedSectionInfo[0],
        lastSelectedSectionInfo[1]
      );

      for (
        let i = selectedSectionInfo.start;
        i <= selectedSectionInfo.end;
        i++
      ) {
        prevState.delete(i);
      }
    }

    return new Map([...prevState, ...newState]);
  });
}

/**
 * click, shift+click한 지점을 index ascending order로 정렬해 반환합니다.
 * @param lastClickInfo
 * @param clickedData
 * @returns
 */
export function getSelectionInfo(
  lastClickInfo: SelectedItemInfo,
  clickedData: SelectedItemInfo
) {
  if (lastClickInfo.index < clickedData.index) {
    return {
      start: lastClickInfo.index,
      end: clickedData.index,
    };
  } else {
    return {
      start: clickedData.index,
      end: lastClickInfo.index,
    };
  }
}

/**
 * ectopicType에 따른 checkbox init 값
 *
 * @param ectopicType
 * @returns
 */
export function getCheckBoxByEctopicType(
  ectopicType: EctopicType
): [boolean] | [boolean, boolean] | undefined {
  if (ectopicType === ECTOPIC_TYPE.ISOLATE) {
    return [true];
  } else if (ectopicType === ECTOPIC_TYPE.COUPLET) {
    return [true, true];
  }
}

/**
 * keyDown validation list
 */
export function returnPressedComposeKey(e: React.KeyboardEvent) {
  // macOs -> control: control, option: alt, command: meta
  // window -> control: control, alt: alt,
  const { metaKey, ctrlKey, altKey, shiftKey } = e;

  const isKeydownWithoutComposeKey =
    !shiftKey && !metaKey && !ctrlKey && !altKey;
  let composeKey: ComposeKey = ComposeKey.none;

  if (isKeydownWithoutComposeKey) {
    composeKey = ComposeKey.none;
  } else if (ctrlKey || metaKey) {
    composeKey = ComposeKey.ctrlKey;
  } else if (altKey) {
    composeKey = ComposeKey.altKey;
  } else if (shiftKey) {
    composeKey = ComposeKey.shiftKey;
  }
  return composeKey;
}

export function isKeydownWithoutComposeKey(composeKey: string): boolean {
  return composeKey === ComposeKey.none;
}
export function isKeydownWithCtrl(composeKey: string): boolean {
  return composeKey === ComposeKey.ctrlKey;
}
export function isKeydownWithAlt(composeKey: string): boolean {
  return composeKey === ComposeKey.altKey;
}
export function isKeydownWithShift(composeKey: string): boolean {
  return composeKey === ComposeKey.shiftKey;
}

export function getRRIRatio({
  comparisonArray,
  referencePoint,
}: {
  comparisonArray: (number | null)[];
  referencePoint: number | null;
}): (number | string)[] {
  const result: (number | string)[] = comparisonArray.map((v) => {
    if (referencePoint === null || v === null) return 'N/A';

    const value: number = Number((referencePoint / v).toFixed(2));
    return isFinite(value) && value > 0 ? value : 'N/A';
  });

  return result;
}

export const uiInteractionMap = {
  [UI_INTERACTION.CLICK]({
    clickedData,
    ectopicType,
    setSelectedItemList,
    setLastSelectedSectionInfo,
  }: {
    clickedData: SelectedItemInfo;
    ectopicType: EctopicType;
    setSelectedItemList: React.Dispatch<
      React.SetStateAction<Map<number, SelectedItemInfo>>
    >;
    setLastSelectedSectionInfo: React.Dispatch<
      React.SetStateAction<LastSelectedSectionInfo>
    >;
  }) {
    ReactDOM.unstable_batchedUpdates(() => {
      clickedData.checkbox = getCheckBoxByEctopicType(ectopicType) as Checkbox;
      const selectItemInfo: SelectedItemInfo = {
        index: clickedData.index,
        matrix: clickedData.matrix,
        checkbox: clickedData.checkbox,
      };
      const clickCaseSelectedItemList = new Map([
        [clickedData.index, selectItemInfo],
      ]);

      setSelectedItemList(clickCaseSelectedItemList);
      setLastSelectedSectionInfo([selectItemInfo, undefined]);
    });
  },
  [UI_INTERACTION.SHIFT_CLICK]({
    clickedData,
    ectopicType,
    chartItemInfo,
    selectedItemList,
    lastSelectedSectionInfo,
    setSelectedItemList,
    setLastSelectedSectionInfo,
  }: {
    clickedData: SelectedItemInfo;
    ectopicType: EctopicType;
    chartItemInfo: BeatEventSelectionInfo;
    selectedItemList: Map<number, SelectedItemInfo>;
    lastSelectedSectionInfo: LastSelectedSectionInfo;
    setSelectedItemList: React.Dispatch<
      React.SetStateAction<Map<number, SelectedItemInfo>>
    >;
    setLastSelectedSectionInfo: React.Dispatch<
      React.SetStateAction<LastSelectedSectionInfo>
    >;
  }) {
    ReactDOM.unstable_batchedUpdates(() => {
      makeSetSelectedItemList({
        selectedItemList,
        lastSelectedSectionInfo,
        movedSelectedItem: clickedData,
        chartItemInfo,
        setSelectedItemList,
        ectopicType,
      });

      clickedData.checkbox = getCheckBoxByEctopicType(ectopicType);
      const selectItemInfo = {
        index: clickedData.index,
        matrix: clickedData.matrix,
        checkbox: clickedData.checkbox,
      };

      setLastSelectedSectionInfo((prev: LastSelectedSectionInfo) => {
        return [prev[0], selectItemInfo] as LastSelectedSectionInfo;
      });
    });
  },
  [UI_INTERACTION.META_CLICK]({
    clickedData,
    ectopicType,
    setSelectedItemList,
    setLastSelectedSectionInfo,
  }: {
    clickedData: SelectedItemInfo;
    ectopicType: EctopicType;
    setSelectedItemList: React.Dispatch<
      React.SetStateAction<Map<number, SelectedItemInfo>>
    >;
    setLastSelectedSectionInfo: React.Dispatch<
      React.SetStateAction<LastSelectedSectionInfo>
    >;
  }) {
    ReactDOM.unstable_batchedUpdates(() => {
      clickedData.checkbox = getCheckBoxByEctopicType(ectopicType);
      const selectItemInfo = {
        index: clickedData.index,
        matrix: clickedData.matrix,
        checkbox: clickedData.checkbox,
      };

      setSelectedItemList((prev: Map<number, SelectedItemInfo>) => {
        const existItem = prev.has(clickedData.index);

        if (existItem) {
          const newState = new Map(prev);
          newState.delete(clickedData.index);
          return newState;
        } else {
          return new Map([...prev, [selectItemInfo.index, selectItemInfo]]);
        }
      });
      setLastSelectedSectionInfo([selectItemInfo, undefined]);
    });
  },

  moveFeature: {
    moveInit({
      keyDownEvent,
      setSelectedItemList,
      setLastSelectedSectionInfo,
    }: {
      keyDownEvent: onKeyDown;
      setSelectedItemList: React.Dispatch<
        React.SetStateAction<Map<number, SelectedItemInfo>>
      >;
      setLastSelectedSectionInfo: React.Dispatch<
        React.SetStateAction<LastSelectedSectionInfo>
      >;
    }) {
      const isPressKeyOneTwoKey =
        keyDownEvent.key === ChartBoxNumber.KeyOne ||
        keyDownEvent.key === ChartBoxNumber.KeyTwo;
      const isPressedMetaKey = keyDownEvent.ctrlKey || keyDownEvent.metaKey;
      const init: SelectedItemInfo = {
        index: 0,
        matrix: [0, 0],
        checkbox: [true, true],
      };

      if (isPressKeyOneTwoKey) return;
      if (isPressedMetaKey) {
        return setLastSelectedSectionInfo([
          {
            index: 0,
            matrix: [0, 0],
            checkbox: [false, false],
          },
          undefined,
        ]);
      } else {
        return ReactDOM.unstable_batchedUpdates(() => {
          setLastSelectedSectionInfo([init, undefined]);
          setSelectedItemList(new Map([[0, init]]));
        });
      }
    },
    [UI_INTERACTION.ARROW_KEYS]() {},
    [UI_INTERACTION.SHIFT_ARROW_KEYS]() {},
    [UI_INTERACTION.META_ARROW_KEYS]() {},
    move({
      keyDownEvent,
      triggerType,
      //
      chartList,
      chartItemInfo,
      moveBasis,
      ectopicType,
      //
      selectedItemList,
      lastSelectedSectionInfo,
      setSelectedItemList,
      setLastSelectedSectionInfo,
      //
      panelType,
      handleSetPage,
    }: {
      keyDownEvent: onKeyDown;
      chartItemInfo: BeatEventSelectionInfo;
      chartList: ChartListItem[];
      triggerType: React.MutableRefObject<ShapeReviewRawAndEventCalledCase>;
      moveBasis: SelectedItemInfo;
      ectopicType: EctopicType;
      selectedItemList: Map<number, SelectedItemInfo>;
      lastSelectedSectionInfo: LastSelectedSectionInfo;
      setSelectedItemList: React.Dispatch<
        React.SetStateAction<Map<number, SelectedItemInfo>>
      >;
      setLastSelectedSectionInfo: React.Dispatch<
        React.SetStateAction<LastSelectedSectionInfo>
      >;
      panelType: ActivePanelType;
      handleSetPage: React.Dispatch<
        React.SetStateAction<{
          panelType: ActivePanelType;
          setPageType: POSITION_MOVE_TYPE;
        }>
      >;
    }) {
      const keyboardDirection: string = keyDownEvent.key;
      const composeKey: ComposeKey = returnPressedComposeKey(keyDownEvent);

      const basisOperand: Record<keyboardDirection, operandType> = {
        ArrowUp: [-1, 0],
        ArrowDown: [1, 0],
        ArrowLeft: [0, -1],
        ArrowRight: [0, 1],
      };
      const moveOperand = basisOperand[keyboardDirection as keyboardDirection];
      const info = {
        baseRowNumber: moveBasis.matrix[0],
        baseColumnNumber: moveBasis.matrix[1],
        targetRowNumber: moveBasis.matrix[0] + moveOperand[0],
        targetColumnNumber: moveBasis.matrix[1] + moveOperand[1],
      };
      const movedChartIndex =
        info.targetRowNumber * chartItemInfo.columnNumber +
        info.targetColumnNumber;

      let movedSelectedItem: SelectedItemInfo = {
        index: movedChartIndex,
        matrix: [
          Math.floor(movedChartIndex / chartItemInfo.columnNumber),
          movedChartIndex % chartItemInfo.columnNumber,
        ],
        checkbox: undefined,
      };

      const maxIndexOfPanel =
        chartItemInfo.columnNumber * chartItemInfo.rowNumber - 1;

      // validation
      const resultValidation = validationKeyDown({
        movedChartIndex,
        maxIndexOfPanel,
        chartList,
        isKeydownWithoutComposeKey: isKeydownWithoutComposeKey(composeKey),
        isKeydownWithCtrl: isKeydownWithCtrl(composeKey),
        isKeydownWithShift: isKeydownWithShift(composeKey),
        panelType,
        handleSetPage,
      });

      if (resultValidation.length !== 0) {
        for (const validation of resultValidation) {
          switch (validation.type) {
            case validationKeyDownType.UNDER_PANEL_SIZE_WITH_SHIFT_KEY:
              break;
            case validationKeyDownType.OVER_PANEL_SIZE_WITH_CTRL_KEY:
            case validationKeyDownType.OVER_PANEL_SIZE_WITH_SHIFT_KEY:
              const chartListLength = chartList.length - 1;
              movedSelectedItem = {
                index: chartListLength,
                matrix: [
                  Math.floor(chartListLength / chartItemInfo.columnNumber),
                  chartListLength % chartItemInfo.columnNumber,
                ],
                checkbox: undefined,
              };
              break;
            case validationKeyDownType.MOVE_PREV_PAGE:
              handleSetPage({
                panelType,
                setPageType: POSITION_MOVE_TYPE.PREV,
              });
              break;
            case validationKeyDownType.MOVE_NEXT_PAGE:
              handleSetPage({
                panelType,
                setPageType: POSITION_MOVE_TYPE.NEXT,
              });
              break;
            case validationKeyDownType.OVER_PANEL_SIZE:
              break;
            case validationKeyDownType.OVER_CHART_LIST_LENGTH:
              break;
            default:
              break;
          }
        }

        if (resultValidation.some((validation) => validation.doReturn)) return;
      }

      triggerType.current = ShapeReviewRawAndEventCalledCase.KEY_EVENT;

      /**
       * # keydown 선택 방법 3가지 case
       * # case1. only arrow keys
       * # case2. shift + arrow keys
       * # case3. ctrl + arrow keys
       */
      // # case1. only arrow keys
      if (isKeydownWithoutComposeKey(composeKey)) {
        ReactDOM.unstable_batchedUpdates(() => {
          movedSelectedItem.checkbox = getCheckBoxByEctopicType(ectopicType);
          setSelectedItemList(new Map([[movedChartIndex, movedSelectedItem]]));
          setLastSelectedSectionInfo([movedSelectedItem, undefined]);
        });
      }

      // # case2. shift + arrow keys
      if (isKeydownWithShift(composeKey)) {
        ReactDOM.unstable_batchedUpdates(() => {
          makeSetSelectedItemList({
            ectopicType,
            chartItemInfo,
            selectedItemList,
            lastSelectedSectionInfo,
            movedSelectedItem,
            setSelectedItemList,
          });
          setLastSelectedSectionInfo((prev: LastSelectedSectionInfo) => {
            return [prev[0], movedSelectedItem] as LastSelectedSectionInfo;
          });
        });
      }

      // # case3. ctrl + arrow keys
      if (isKeydownWithCtrl(composeKey)) {
        setLastSelectedSectionInfo([movedSelectedItem, undefined]);
      }
    },
  },
  checkbox: {
    selectCheckBox({
      keyDownEvent,
      ectopicType,
      moveBasis,
      setSelectedItemList,
      setLastSelectedSectionInfo,
    }: {
      keyDownEvent: onKeyDown;
      ectopicType: EctopicType;
      moveBasis: SelectedItemInfo;
      setSelectedItemList: React.Dispatch<
        React.SetStateAction<Map<number, SelectedItemInfo>>
      >;
      setLastSelectedSectionInfo: React.Dispatch<
        React.SetStateAction<LastSelectedSectionInfo>
      >;
    }) {
      ReactDOM.unstable_batchedUpdates(() => {
        setLastSelectedSectionInfo([moveBasis, undefined]);
        setSelectedItemList((prev: Map<number, SelectedItemInfo>) => {
          let clone: SelectedItemInfo = {
            index: moveBasis.index,
            matrix: moveBasis.matrix,
            checkbox: moveBasis.checkbox,
          };
          const targetItem = prev.get(clone.index);

          if (!!targetItem) {
            if (keyDownEvent.key === ChartBoxNumber.KeyOne) {
              clone.checkbox = [
                !targetItem?.checkbox?.[0],
                !!targetItem?.checkbox?.[1],
              ];
            } else if (keyDownEvent.key === ChartBoxNumber.KeyTwo) {
              clone.checkbox = [
                !!targetItem?.checkbox?.[0],
                !targetItem?.checkbox?.[1],
              ];
            }
          } else {
            if (keyDownEvent.key === ChartBoxNumber.KeyOne) {
              clone.checkbox = [true, false];
            } else if (keyDownEvent.key === ChartBoxNumber.KeyTwo) {
              clone.checkbox = [false, true];
            }
          }

          if (ectopicType === ECTOPIC_TYPE.ISOLATE && clone.checkbox) {
            clone.checkbox = [clone.checkbox[0]];
          } else if (
            ectopicType === ECTOPIC_TYPE.COUPLET &&
            clone.checkbox &&
            clone.checkbox[0] &&
            clone.checkbox[1]
          ) {
            clone.checkbox = [clone.checkbox[0], clone.checkbox[1]];
          }

          if (!clone.checkbox) return new Map(prev);

          const hasSomeTrue = clone.checkbox.some(
            (v: boolean | undefined) => v === true
          );

          if (hasSomeTrue) {
            return new Map([...prev, [moveBasis.index, clone]]);
          } else {
            // checkbox all false case
            const newState = new Map(prev);
            newState.delete(moveBasis.index);
            return new Map(newState);
          }
        });
      });
    },
    toggleSelectAll({
      panelType,
      selectAllState,
      setCheckBoxStatus,
      handleSetSelectAll,
    }: {
      panelType: ActivePanelType;
      selectAllState: CheckBoxAll;
      setCheckBoxStatus: React.Dispatch<React.SetStateAction<CheckBoxStatus>>;
      handleSetSelectAll: React.Dispatch<
        React.SetStateAction<{
          panelType: ActivePanelType;
          selectAllState: CheckBoxAll;
        }>
      >;
    }) {
      const nextSelectAllState =
        selectAllState === CheckBoxAll.None
          ? CheckBoxAll.CheckedAll
          : CheckBoxAll.None;

      handleSetSelectAll({
        panelType,
        selectAllState: nextSelectAllState,
      });

      return;
    },
  },
  pagination({
    keyDownEvent,
    panelType,
    handleSetPage,
  }: {
    keyDownEvent: onKeyDown;
    panelType: ActivePanelType;
    handleSetPage: React.Dispatch<
      React.SetStateAction<{
        panelType: ActivePanelType;
        setPageType: POSITION_MOVE_TYPE;
      }>
    >;
  }) {
    const isLeftBracket = keyDownEvent.key === shortCut['['];
    const setPageType = isLeftBracket
      ? POSITION_MOVE_TYPE.PREV
      : POSITION_MOVE_TYPE.NEXT;

    handleSetPage({
      panelType,
      setPageType,
    });
  },
};

export function getIsEditedOfFormPanel(
  panelPatchedList: any,
  chartInfo: ChartListItem
): boolean {
  return (
    panelPatchedList.includes(chartInfo.formInfo.id) &&
    chartInfo.formInfo.formRankNumber !== 0
  );
}
export function getIsEditedOfEventPanel(
  panelPatchedList: any,
  chartInfo: ChartListItem
): boolean {
  let isEdited = false;

  if (panelPatchedList === editType.ALL) {
    isEdited = true;
  } else if (Array.isArray(panelPatchedList)) {
    isEdited = panelPatchedList.includes(chartInfo.originWaveformIndex);
  }

  return isEdited;
}

export function getSelectedClassName(
  selectedItemList: Map<number, any>,
  chartInfo: ChartListItem
): string {
  return selectedItemList.has(chartInfo.index) ? 'selected' : '';
}

export function getFormLabel(
  chartInfo: ChartListItem,
  filterShapeReviewEventGroupType: IShapeReviewSidePanelEventGroup
) {
  let formLabel;
  const formRankNumber = chartInfo.formInfo.formRankNumber;

  switch (formRankNumber) {
    case 0:
      formLabel = `Edited ${filterShapeReviewEventGroupType.label}`;
      break;
    case -1:
      formLabel = `Etc. ${filterShapeReviewEventGroupType.label}`;
      break;
    default:
      formLabel = `Form ${formRankNumber}`;
  }

  return formLabel;
}

export function getCheckConditions(selectedItem: [number, any] | undefined): {
  isCheckedFirst: boolean;
  isCheckedSecond: boolean;
} {
  let isCheckedFirst = false;
  let isCheckedSecond = false;

  if (!!selectedItem && selectedItem[1].checkbox) {
    isCheckedFirst = selectedItem[1].checkbox[0];
    isCheckedSecond = selectedItem[1].checkbox[1];
  }

  return { isCheckedFirst, isCheckedSecond };
}

export function getSelectedItem(
  selectedItemList: Map<number, any>,
  chartInfo: ChartListItem
) {
  return [...selectedItemList].find(
    (selectedItem) =>
      selectedItem[1].matrix[0] === chartInfo.matrix[0] &&
      selectedItem[1].matrix[1] === chartInfo.matrix[1]
  );
}

export function getFocusState(
  checkBoxStatus: CheckBoxStatus,
  lastClickInfo: SelectedItemInfo | undefined,
  chartInfo: ChartListItem
): boolean {
  let isFocused = false;

  if (
    checkBoxStatus.isIndeterminate === false &&
    checkBoxStatus.isChecked === true
  ) {
    isFocused = false;
  } else {
    isFocused =
      lastClickInfo?.matrix[0] === chartInfo.matrix[0] &&
      lastClickInfo?.matrix[1] === chartInfo.matrix[1];
  }
  return isFocused;
}

export function formatRRIntervalRatio(
  rrIntervalRatioArray: (number | string)[]
) {
  if (rrIntervalRatioArray.length === 1) return rrIntervalRatioArray[0];

  return `${rrIntervalRatioArray[0]} : ${rrIntervalRatioArray[1]}`;
}

export const shapeReviewState = {
  visibleArrangeRequiredTooltip: false,
};

export function getIsSelectedAll({
  selectedItemList,
  panelList,
}: {
  selectedItemList: [number, SelectedItemInfo][];
  panelList: ChartListItem[];
}) {
  const isSameAsSelectedBox = selectedItemList.length === panelList.length; // 선택된 box 개수가 패널 전체 box 개수와 같은지 판단 여부
  const isCheckBoxAllTrue = !selectedItemList.some(
    // 선택된 box 중 하나라도 checkbox가 false인 것이 있는지 판단
    (item) => item[1].checkbox && item[1].checkbox.includes(false)
  );

  const isSelectedAll =
    selectedItemList.length > 0 && isSameAsSelectedBox && isCheckBoxAllTrue;

  return isSelectedAll;
}

export function getIsIndeterminate({
  selectedItemList,
  panelList,
}: {
  selectedItemList: [number, SelectedItemInfo][];
  panelList: ChartListItem[];
}) {
  const isSelectedAll = getIsSelectedAll({
    selectedItemList,
    panelList,
  });
  const isCheckBoxHasTrue = selectedItemList.some(
    // 선택된 box 중 하나라도 checkbox가 true가 있는지 판단
    (item) => item[1].checkbox && item[1].checkbox.includes(true)
  );
  const isIndeterminate =
    selectedItemList.length > 0 && !isSelectedAll && isCheckBoxHasTrue;

  return isIndeterminate;
}

/**
 * event panel에서 event가 모두 수정되었는지 판단
 * couplet event에서 하나의 event(waveformIndex)가 수정되면 수정으로 간주
 * @param param0
 * @returns
 */
export function getIsAllEventEdited({
  poolingDataOfClickedEvent,
  newPatchedEventListOfClickedForm,
}: {
  poolingDataOfClickedEvent: [WaveformIndex, ChartListItem][];
  newPatchedEventListOfClickedForm: WaveformIndex[];
}) {
  for (let arr of poolingDataOfClickedEvent) {
    const index = arr[1].beats.waveformIndex.indexOf(arr[0]);
    const wfi1 = arr[1].beats.waveformIndex[index];
    const wfi2 = arr[1].beats.waveformIndex[index + 1];

    const isPatchedWfi =
      newPatchedEventListOfClickedForm.includes(wfi1) ||
      newPatchedEventListOfClickedForm.includes(wfi2);
    if (!isPatchedWfi) return false;
  }

  return true;
}
